import React, { useState } from "react";
import { useUser } from "../../../context/UserContext";
import { canAccessAdmin } from "../../../utils/roleUtils";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { sanitizeText, sanitizeEmail, sanitizePhone } from "../../../utils/sanitization";
import BaseModal from "../../ui/BaseModal";
import "../../../styles/modern-design-system.css";

// Cross-browser compatibility polyfills
const safeTrim = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
};

const safeToLowerCase = (str) => {
  if (typeof str !== 'string') return '';
  return str.toLowerCase ? str.toLowerCase() : str.replace(/[A-Z]/g, function(match) {
    return String.fromCharCode(match.charCodeAt(0) + 32);
  });
};

const CreateClientModal = ({ isOpen, onClose, onClientCreated }) => {
  const { user } = useUser();
  const { toast } = useToast();
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [relationshipStatus, setRelationshipStatus] = useState("lead");
  const [source, setSource] = useState("website");
  const [communicationPreference, setCommunicationPreference] = useState("email");
  const [preferredDestination, setPreferredDestination] = useState("disney_world");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Access control for premium destinations
  const hasNewDestinationAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length > 0) {
      return `(${phoneNumber}`;
    }
    return phoneNumber;
  };

  // Auto-capitalize function for name fields
  const capitalizeName = (value) => {
    return value
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };


  if (!isOpen) return null;

  // Validation functions - cross-browser compatible
  const validateEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    
    const trimmedEmail = safeTrim(email);
    if (trimmedEmail.length === 0) return false;
    
    // More comprehensive email validation regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    // Additional checks
    if (!emailRegex.test(trimmedEmail)) return false;
    if (trimmedEmail.length > 254) return false; // RFC 5321 limit
    if (trimmedEmail.includes('..')) return false; // No consecutive dots
    if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.')) return false; // No leading/trailing dots
    
    return true;
  };

  const validatePhone = (phone) => {
    if (!phone || !safeTrim(phone)) return true; // Phone is optional
    // Simplified phone regex for better browser compatibility
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,20}$/;
    return phoneRegex.test(phone);
  };

  const validateName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmedName = safeTrim(name);
    if (trimmedName.length < 2) return false;
    if (trimmedName.length > 100) return false;
    // Check if name contains only letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    return nameRegex.test(trimmedName);
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate full name - cross-browser safe
    if (!fullname || !safeTrim(fullname)) {
      newErrors.fullname = "Full name is required.";
    } else if (!validateName(fullname)) {
      newErrors.fullname = "Please enter a valid name (2-100 characters, letters only).";
    }

    // Validate email - cross-browser safe
    if (!email || !safeTrim(email)) {
      newErrors.email = "Email address is required.";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Validate phone (optional) - cross-browser safe
    if (phone && safeTrim(phone) && !validatePhone(sanitizePhone(phone))) {
      newErrors.phone = "Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890).";
    }

    setErrors(newErrors);
    // Cross-browser compatible object key counting
    let keyCount = 0;
    for (let key in newErrors) {
      if (newErrors.hasOwnProperty(key)) {
        keyCount++;
      }
    }
    return keyCount === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (loading) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({}); // Clear any previous errors
    
    try {
      // Cross-browser compatible API call
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const res = await fetch(`${apiUrl}/api/clients`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullname: safeTrim(sanitizeText(fullname)),
          email: safeToLowerCase(safeTrim(sanitizeEmail(email))),
          phone: safeTrim(sanitizePhone(phone)) || null,
          relationship_status: relationshipStatus,
          source: source,
          communication_preference: communicationPreference,
          preferred_destination: preferredDestination,
          lifetime_value: 0,
          total_trips: 0,
        }),
      });

      // Cross-browser compatible JSON parsing
      let data;
      if (res.json) {
        data = await res.json();
      } else {
        // Fallback for very old browsers
        const text = await res.text();
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error("Invalid response format");
        }
      }
      if (!res.ok) {
        // Handle specific error cases - cross-browser compatible
        if (res.status === 409 || 
            (data.error && (data.error.includes('already exists') || data.error.includes('duplicate')))) {
          setErrors({ email: "A client with this email address already exists." });
          toast.error("A client with this email address already exists.", { duration: 5000 });
          setLoading(false); // Stop loading so user can fix the error
          return; // Keep modal open
        }
        throw new Error(data.error || "Failed to create client");
      }

      // Success! Close modal immediately and show toast
      toast.success("Client created successfully!", { duration: 4000 });
      
      // Reset form
      setFullname("");
      setEmail("");
      setPhone("");
      setErrors({});
      
      // Close modal immediately
      onClose();
      
      // Trigger callback after a brief delay to allow modal to close first
      setTimeout(() => {
        onClientCreated(data);
      }, 100);
    } catch (err) {
      console.error("Error creating client:", err);
      
      // Keep modal open and show error
      let errorMessage = "Something went wrong. Please try again.";
      
      // Cross-browser compatible error handling
      if (err.message && (err.message.includes('already exists') || err.message.includes('duplicate'))) {
        setErrors({ email: "A client with this email address already exists." });
        errorMessage = "A client with this email address already exists.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Show error toast
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset form
      setFullname("");
      setEmail("");
      setPhone("");
      setRelationshipStatus("lead");
      setSource("website");
      setCommunicationPreference("email");
      setPreferredDestination("disney_world");
      setErrors({});
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="lg" showClose={false}>
      <div className="p-5 overflow-y-auto max-h-[75vh]">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h2 className="mb-1 text-xl font-bold text-gray-900">
              Add New Client
            </h2>
            <p className="text-xs text-gray-500">
              Enter your client's information to add them to your database
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2.5">
          <div>
            <label htmlFor="fullname" className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="fullname"
                type="text"
                value={fullname}
                onChange={(e) => {
                  const capitalizedName = capitalizeName(sanitizeText(e.target.value));
                  setFullname(capitalizedName);
                  if (errors.fullname) {
                    setErrors(prev => ({ ...prev, fullname: undefined }));
                  }
                }}
                required
                placeholder="Enter client's full name"
                disabled={loading}
                maxLength={100}
                className={`block w-full px-3 text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 transition-all placeholder-gray-400 appearance-none ${
                  errors.fullname ? 'border-red-500 focus:ring-red-500' :
                  fullname ? 'border-blue-400 focus:ring-blue-500' : 'focus:ring-blue-500'
                }`}
                style={{
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  height: '40px'
                }}
                aria-describedby={`${fullname ? 'fullname-help' : ''}`}
                aria-invalid={errors.fullname ? 'true' : 'false'}
                autoComplete="off"
              />
              
              {/* Validation Icons */}
              {errors.fullname && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
              {fullname && !errors.fullname && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
            </div>
            
            {errors.fullname && (
              <p id="fullname-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                <AlertCircle className="w-4 h-4" />
                <span>{errors.fullname}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  const lowercasedEmail = sanitizeEmail(e.target.value.toLowerCase());
                  setEmail(lowercasedEmail);
                  if (errors.email) {
                    setErrors(prev => ({ ...prev, email: undefined }));
                  }
                }}
                required
                placeholder="Enter client's email address"
                disabled={loading}
                className={`block w-full px-3 text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 transition-all placeholder-gray-400 appearance-none ${
                  errors.email ? 'border-red-500 focus:ring-red-500' :
                  email ? 'border-blue-400 focus:ring-blue-500' : 'focus:ring-blue-500'
                }`}
                style={{
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  height: '40px'
                }}
                aria-describedby={`${errors.email ? 'email-help' : ''}`}
                aria-invalid={errors.email ? 'true' : 'false'}
                autoComplete="off"
              />
              
              {/* Validation Icons */}
              {errors.email && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
              {email && !errors.email && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
            </div>
            
            {errors.email && (
              <p id="email-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                <AlertCircle className="w-4 h-4" />
                <span>{errors.email}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Phone Number (Optional)
            </label>
            <div className="relative">
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setPhone(formatted);
                  if (errors.phone) {
                    setErrors(prev => ({ ...prev, phone: undefined }));
                  }
                }}
                placeholder="(###) ###-####"
                disabled={loading}
                maxLength={14}
                className={`block w-full px-3 text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 transition-all placeholder-gray-400 appearance-none ${
                  errors.phone ? 'border-red-500 focus:ring-red-500' :
                  phone ? 'border-blue-400 focus:ring-blue-500' : 'focus:ring-blue-500'
                }`}
                style={{
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  height: '40px'
                }}
                aria-describedby={`${errors.phone ? 'phone-help' : ''}`}
                aria-invalid={errors.phone ? 'true' : 'false'}
                autoComplete="off"
              />
              
              {/* Validation Icons */}
              {errors.phone && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
              {phone && !errors.phone && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
            </div>
            
            {errors.phone && (
              <p id="phone-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                <AlertCircle className="w-4 h-4" />
                <span>{errors.phone}</span>
              </p>
            )}
          </div>

          {/* CRM Fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="relationshipStatus" className="flex gap-1 items-center mb-1 text-sm font-semibold leading-tight text-gray-800">
                Relationship Status
                <span className="text-gray-400 cursor-help" title="Lead = new contact. Prospect = discussing a trip. Customer = has booked or completed a trip.">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path strokeWidth="2" d="M12 16v-4m0-4h.01"/></svg>
                </span>
              </label>
              <div className="relative">
                <select
                  id="relationshipStatus"
                  value={relationshipStatus}
                  onChange={(e) => setRelationshipStatus(e.target.value)}
                  className="px-3 pr-10 w-full h-10 text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                                    disabled={loading}
                >
                  <option value="lead">Lead</option>
                  <option value="prospect">Prospect</option>
                  <option value="customer">Customer</option>
                </select>
                {/* Chevron icon */}
                <svg
                  className="absolute right-3 top-1/2 w-5 h-5 text-gray-300 -translate-y-1/2 pointer-events-none"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div>
              <label htmlFor="source" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Lead Source
              </label>
              <div className="relative">
                <select
                  id="source"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="px-3 pr-10 w-full h-10 text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                                    disabled={loading}
                >
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="facebook_ad">Facebook Ad</option>
                  <option value="google_search">Google Search</option>
                  <option value="social_media">Social Media</option>
                  <option value="phone">Phone</option>
                </select>
                {/* Chevron icon */}
                <svg
                  className="absolute right-3 top-1/2 w-5 h-5 text-gray-300 -translate-y-1/2 pointer-events-none"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div>
              <label htmlFor="communicationPreference" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Communication Preference
              </label>
              <div className="relative">
                <select
                  id="communicationPreference"
                  value={communicationPreference}
                  onChange={(e) => setCommunicationPreference(e.target.value)}
                  className="px-3 pr-10 w-full h-10 text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                                    disabled={loading}
                >
                  <option value="email">Email</option>
                  <option value="text">Text</option>
                  <option value="phone">Phone</option>
                </select>
                {/* Chevron icon */}
                <svg
                  className="absolute right-3 top-1/2 w-5 h-5 text-gray-300 -translate-y-1/2 pointer-events-none"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div>
              <label htmlFor="preferredDestination" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Preferred Destination
              </label>
              <div className="relative">
                <select
                  id="preferredDestination"
                  value={preferredDestination}
                  onChange={(e) => setPreferredDestination(e.target.value)}
                  className="px-3 pr-10 w-full h-10 text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                                    disabled={loading}
                >
                  <option value="disney_world">Walt Disney World Resort</option>
                  {hasNewDestinationAccess() && (
                    <>
                      <option value="disneyland">Disneyland Resort California</option>
                      <option value="universal_orlando">Universal Orlando Resort</option>
                      <option value="disney_cruise">Disney Cruise Line</option>
                    </>
                  )}
                </select>
                {/* Chevron icon */}
                <svg
                  className="absolute right-3 top-1/2 w-5 h-5 text-gray-300 -translate-y-1/2 pointer-events-none"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex justify-center items-center">
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Creating Client...
                </div>
              ) : (
                "Create Client"
              )}
            </button>
          </div>
        </form>
      </div>
    </BaseModal>
  );
};

export default CreateClientModal;